import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Send,
  Plus,
  X,
  ArrowLeft,
  Paperclip,
  Type,
  Bold,
  Italic,
  Underline,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  Link,
  Image,
  Save,
  FileText,
} from 'lucide-react';
import { EmailSignatureSelector } from './EmailSignatureSelector';
import { RichTextEditor } from './RichTextEditor';
import { EmailTemplates, EmailTemplate } from './EmailTemplates';

interface EmailSignature {
  id: number;
  signatureName: string;
  signatureHtml: string;
  signatureText: string;
  isDefault: boolean;
  isActive: boolean;
}

interface EmailComposerProps {
  customerId: number;
  customerName?: string;
  customerEmail?: string;
  onClose: () => void;
  replyToConversationId?: number;
  forwardFromMessageId?: number;
  prefillData?: {
    to?: string;
    cc?: string;
    subject?: string;
    body?: string;
  };
}

interface EmailDraft {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  bodyHtml: string;
  signatureId: number | null;
  priority: 'low' | 'normal' | 'high';
  attachments: File[];
}

export function EmailComposer({
  customerId,
  customerName,
  customerEmail,
  onClose,
  replyToConversationId,
  forwardFromMessageId,
  prefillData,
}: EmailComposerProps) {
  const { toast } = useToast();
  const [emailDraft, setEmailDraft] = useState<EmailDraft>({
    to: prefillData?.to ? [prefillData.to] : customerEmail ? [customerEmail] : [],
    cc: prefillData?.cc ? [prefillData.cc] : [],
    bcc: [],
    subject: prefillData?.subject || '',
    body: prefillData?.body || '',
    bodyHtml: '',
    signatureId: null,
    priority: 'normal',
    attachments: [],
  });
  const [currentDraftId, setCurrentDraftId] = useState<number | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');
  const [showSignatureManager, setShowSignatureManager] = useState(false);
  const [isDraft, setIsDraft] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);

  // Auto-save draft every 30 seconds
  useEffect(() => {
    if (!autoSaveEnabled || (!emailDraft.subject && !emailDraft.body)) return;

    const timer = setTimeout(() => {
      saveDraftMutation.mutate(emailDraft);
    }, 30000); // 30 seconds

    return () => clearTimeout(timer);
  }, [emailDraft, autoSaveEnabled]);

  // Fetch email signatures
  const { data: signatures, isLoading: signaturesLoading } = useQuery({
    queryKey: ['/api/email-signatures'],
    queryFn: () => apiRequest<EmailSignature[]>('GET', '/api/email-signatures'),
  });

  // Set default signature when signatures load
  useEffect(() => {
    if (signatures && signatures.length > 0 && !emailDraft.signatureId) {
      const defaultSignature = signatures.find((s) => s.isDefault) || signatures[0];
      setEmailDraft((prev) => ({ ...prev, signatureId: defaultSignature.id }));
    }
  }, [signatures, emailDraft.signatureId]);

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: (emailData: EmailDraft) => {
      return apiRequest('POST', '/api/emails/send', {
        customerId,
        to: emailData.to,
        cc: emailData.cc,
        bcc: emailData.bcc,
        subject: emailData.subject,
        body: emailData.body,
        bodyHtml: emailData.bodyHtml,
        signatureId: emailData.signatureId,
        priority: emailData.priority,
        replyToConversationId,
        forwardFromMessageId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/emails/conversations'] });
      toast({
        title: 'Email sent',
        description: 'Your email has been sent successfully.',
      });
      onClose();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to send email: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  // Save draft mutation
  const saveDraftMutation = useMutation({
    mutationFn: async (emailData: EmailDraft) => {
      const draftData = {
        customerId,
        conversationId: replyToConversationId,
        subject: emailData.subject,
        bodyHtml: emailData.bodyHtml || emailData.body,
        bodyText: emailData.body,
        recipients: emailData.to,
        cc: emailData.cc,
        bcc: emailData.bcc,
        signatureId: emailData.signatureId,
      };

      if (currentDraftId) {
        // Update existing draft
        return apiRequest('PUT', `/api/emails/drafts/${currentDraftId}`, draftData);
      } else {
        // Create new draft
        const response = await apiRequest<{ id: number }>('POST', '/api/emails/drafts', draftData);
        setCurrentDraftId(response.id);
        return response;
      }
    },
    onSuccess: () => {
      setLastSavedAt(new Date());
      setIsDraft(true);
      toast({
        title: 'Draft saved',
        description: 'Your email draft has been saved.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: `Failed to save draft: ${error.message}`,
        variant: 'destructive',
      });
    },
  });

  const handleAddEmail = (type: 'to' | 'cc' | 'bcc', email: string) => {
    if (!email.trim() || !email.includes('@')) return;

    const cleanEmail = email.trim().toLowerCase();
    setEmailDraft((prev) => ({
      ...prev,
      [type]: [...prev[type], cleanEmail],
    }));

    // Clear input
    if (type === 'to') setToInput('');
    if (type === 'cc') setCcInput('');
    if (type === 'bcc') setBccInput('');
  };

  const handleRemoveEmail = (type: 'to' | 'cc' | 'bcc', index: number) => {
    setEmailDraft((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent, type: 'to' | 'cc' | 'bcc', value: string) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddEmail(type, value);
    }
  };

  const handleSendEmail = () => {
    // Validation
    if (emailDraft.to.length === 0) {
      toast({
        title: 'Missing recipient',
        description: 'Please add at least one recipient.',
        variant: 'destructive',
      });
      return;
    }

    if (!emailDraft.subject.trim()) {
      toast({
        title: 'Missing subject',
        description: 'Please add a subject line.',
        variant: 'destructive',
      });
      return;
    }

    if (!emailDraft.body.trim()) {
      toast({
        title: 'Missing message',
        description: 'Please add a message body.',
        variant: 'destructive',
      });
      return;
    }

    sendEmailMutation.mutate(emailDraft);
  };

  const handleSaveDraft = () => {
    saveDraftMutation.mutate(emailDraft);
  };

  const getSelectedSignature = () => {
    if (!signatures || !emailDraft.signatureId) return null;
    return signatures.find((s) => s.id === emailDraft.signatureId);
  };

  const renderEmailsInput = (
    type: 'to' | 'cc' | 'bcc',
    emails: string[],
    input: string,
    setInput: (value: string) => void,
    placeholder: string
  ) => (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {emails.map((email, index) => (
          <Badge key={index} variant="secondary" className="flex items-center gap-1">
            {email}
            <button
              onClick={() => handleRemoveEmail(type, index)}
              className="ml-1 hover:text-red-500"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <Input
          type="email"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => handleKeyPress(e, type, input)}
          onBlur={() => {
            if (input.trim()) handleAddEmail(type, input);
          }}
          placeholder={placeholder}
          className="flex-1 min-w-[200px] border-none shadow-none p-0 h-auto focus-visible:ring-0"
        />
      </div>
    </div>
  );

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3 border-b">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onClose} className="p-2">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <CardTitle className="text-lg">
              {replyToConversationId ? 'Reply' : forwardFromMessageId ? 'Forward' : 'New Email'}
            </CardTitle>
            {customerName && <Badge variant="outline">{customerName}</Badge>}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowTemplates(true)}
              className="flex items-center gap-2"
            >
              <FileText className="h-4 w-4" />
              Templates
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSaveDraft}
              disabled={saveDraftMutation.isPending}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              {isDraft ? 'Saved' : 'Save Draft'}
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={sendEmailMutation.isPending}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {sendEmailMutation.isPending ? 'Sending...' : 'Send'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-auto p-0">
        <div className="p-6 space-y-4">
          {/* Recipients */}
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">To</Label>
              <div className="border rounded-md p-2 min-h-[40px] mt-1">
                {renderEmailsInput(
                  'to',
                  emailDraft.to,
                  toInput,
                  setToInput,
                  'Enter email addresses'
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 text-sm">
              {!showCc && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCc(true)}
                  className="h-auto p-1 text-muted-foreground hover:text-foreground"
                >
                  CC
                </Button>
              )}
              {!showBcc && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowBcc(true)}
                  className="h-auto p-1 text-muted-foreground hover:text-foreground"
                >
                  BCC
                </Button>
              )}
            </div>

            {showCc && (
              <div>
                <Label className="text-sm font-medium">CC</Label>
                <div className="border rounded-md p-2 min-h-[40px] mt-1">
                  {renderEmailsInput(
                    'cc',
                    emailDraft.cc,
                    ccInput,
                    setCcInput,
                    'Enter CC email addresses'
                  )}
                </div>
              </div>
            )}

            {showBcc && (
              <div>
                <Label className="text-sm font-medium">BCC</Label>
                <div className="border rounded-md p-2 min-h-[40px] mt-1">
                  {renderEmailsInput(
                    'bcc',
                    emailDraft.bcc,
                    bccInput,
                    setBccInput,
                    'Enter BCC email addresses'
                  )}
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Subject */}
          <div>
            <Label className="text-sm font-medium">Subject</Label>
            <Input
              value={emailDraft.subject}
              onChange={(e) => setEmailDraft((prev) => ({ ...prev, subject: e.target.value }))}
              placeholder="Enter subject"
              className="mt-1"
            />
          </div>

          <Separator />

          {/* Body */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Message</Label>
            <RichTextEditor
              value={emailDraft.bodyHtml || emailDraft.body}
              onChange={(html, text) => {
                setEmailDraft((prev) => ({
                  ...prev,
                  body: text,
                  bodyHtml: html,
                }));
              }}
              placeholder="Type your message..."
              minHeight="300px"
            />
          </div>

          <Separator />

          {/* Signature Selection */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Email Signature</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowSignatureManager(true)}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Manage Signatures
              </Button>
            </div>

            <EmailSignatureSelector
              signatures={signatures || []}
              selectedId={emailDraft.signatureId}
              onSelect={(id) => setEmailDraft((prev) => ({ ...prev, signatureId: id }))}
            />

            {/* Signature Preview */}
            {getSelectedSignature() && (
              <div className="p-3 border rounded-md bg-muted/30">
                <div className="text-xs text-muted-foreground mb-2">Signature Preview:</div>
                <div
                  className="text-sm prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{
                    __html:
                      getSelectedSignature()?.signatureHtml ||
                      getSelectedSignature()?.signatureText ||
                      '',
                  }}
                />
              </div>
            )}
          </div>

          {/* Priority & Options */}
          <div className="flex items-center gap-4 pt-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm">Priority:</Label>
              <div className="flex gap-1">
                {(['low', 'normal', 'high'] as const).map((priority) => (
                  <Button
                    key={priority}
                    variant={emailDraft.priority === priority ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setEmailDraft((prev) => ({ ...prev, priority }))}
                    className="capitalize"
                  >
                    {priority}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContent>

      {/* Signature Manager Dialog */}
      <Dialog open={showSignatureManager} onOpenChange={setShowSignatureManager}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Manage Email Signatures</DialogTitle>
            <DialogDescription>Create and manage your email signatures</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Signature management will be available in the next update.
            </p>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSignatureManager(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Email Templates</DialogTitle>
            <DialogDescription>
              Select a template to quickly compose your email
            </DialogDescription>
          </DialogHeader>
          <EmailTemplates
            onSelectTemplate={(template: EmailTemplate) => {
              setEmailDraft((prev) => ({
                ...prev,
                subject: template.subject,
                body: (template.body || '').replace('[Customer Name]', customerName || 'Customer'),
                bodyHtml: template.bodyHtml || template.body || '',
              }));
              setShowTemplates(false);
              toast({
                title: 'Template Applied',
                description: `"${template.name}" template has been applied.`,
              });
            }}
            onClose={() => setShowTemplates(false)}
          />
        </DialogContent>
      </Dialog>
    </Card>
  );
}
